/**
 * One live session's report in Season, at its own address. A phone and an iPad upright read it down the page; on its
 * side the past sessions stand at the left, the report in the middle and the replay, moments and reactions at the right.
 */
import { type ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { WebinarDetail, WebinarLive } from "../../../shared/types";
import { labelParts } from "../../../shared/live-time";
import { DockedBar } from "../../components/season/DockedBar";
import { Empty } from "../../components/season/Empty";
import { Fold } from "../../components/season/Fold";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { GroupHead } from "../../components/season/PageHead";
import { Pane, Panes } from "../../components/season/Panes";
import { PushedNav } from "../../components/season/PushedNav";
import { Button } from "../../components/ui/Button";
import "../../styles/season-rooms.css";
import { longDay, longDayTime, sessionReportPath } from "./liveWords";
import { ReportMain, ReportRest, ReportSheets } from "./SeasonReport";
import { useSessionPage } from "./useSessionPage";
import { useSessionReport } from "./useSessionReport";

/** The past sessions the iPad's left pane shows before the earlier ones fold away. */
const SHOWN = 4;

function PastSessions({ detail, current }: { detail: WebinarDetail; current: string }) {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const series = detail.live?.series;
  if (!series) return null;
  const ran = series.sessions.filter((s) => s.state === "ended").toSorted((a, b) => b.seq - a.seq);
  const list = all ? ran : ran.slice(0, SHOWN);
  const first = ran.at(-1);
  return (
    <>
      <GroupHead title="Past sessions" />
      <GroupedList className="rm-dl">
        {list.map((s) => {
          const p = labelParts(s.startsAt, s.timezone, "short");
          const pct = s.registered ? Math.round((s.cameLive / s.registered) * 100) : 0;
          return (
            <ListRow
              key={s.id}
              lead={
                <span className="rm-db">
                  <span>{p?.month ?? ""}</span>
                  <b>{p?.day ?? ""}</b>
                </span>
              }
              title={longDay(s.startsAt, s.timezone)}
              sub={`${s.cameLive.toLocaleString("en-US")} of ${s.registered.toLocaleString("en-US")} came · ${pct}%`}
              on={s.id === current}
              onClick={() => navigate(sessionReportPath(detail.id, s.id))}
            />
          );
        })}
      </GroupedList>
      {ran.length > SHOWN && !all ? (
        <Fold
          title={`${ran.length - SHOWN} earlier ${ran.length - SHOWN === 1 ? "session" : "sessions"}`}
          line={first ? `From ${longDay(first.startsAt, first.timezone)}` : undefined}
          isOpen={false}
          onClick={() => setAll(true)}
        />
      ) : null}
    </>
  );
}

export function SeasonSession({ layout }: { layout: SeasonLayout }) {
  const { id = "", sessionId = "" } = useParams<{ id: string; sessionId: string }>();
  const navigate = useNavigate();
  const { detail, live, phase } = useSessionPage(id, sessionId);
  const back = `/rooms/${id}/sessions`;
  const nav = <PushedNav back={{ to: back, label: layout === "phone" ? "Sessions" : (detail?.title ?? "Sessions") }} />;

  if (phase === "loading") {
    return (
      <Page layout={layout} narrow className="rm">
        {nav}
        <p className="k-sub" aria-busy="true">
          Loading
        </p>
      </Page>
    );
  }
  if (phase === "error" || !detail || !live) {
    return (
      <Page layout={layout} narrow className="rm">
        {nav}
        <Empty title="We couldn't load this session">
          <Button onClick={() => navigate(back)}>Back to the sessions</Button>
        </Empty>
      </Page>
    );
  }
  if (live.state !== "ended") {
    // The address exists whether or not the session has run, so a bookmark for a week to come says so.
    return (
      <Page layout={layout} narrow className="rm">
        {nav}
        <Empty title="Nothing to report yet">
          <p>{live.state === "live" ? "It's on now. The report is here once you end it." : `It runs ${longDayTime(live.startsAt, live.timezone)}.`}</p>
          <Button onClick={() => navigate(back)}>Back to the sessions</Button>
        </Empty>
      </Page>
    );
  }
  return <Report layout={layout} detail={detail} live={live} nav={nav} />;
}

function Report({ layout, detail, live, nav }: { layout: SeasonLayout; detail: WebinarDetail; live: WebinarLive; nav: ReactNode }) {
  const r = useSessionReport(detail.id, live, !!detail.live?.series);
  const time = labelParts(live.startsAt, live.timezone, "long")?.time;
  const title = (
    <>
      <h1 className="k-h1">{`${longDay(live.startsAt, live.timezone)}${time ? `, ${time}` : ""}`}</h1>
      <p className="k-meta rm-ctx">{`${detail.title} · Session ${live.seq}`}</p>
    </>
  );
  const sheets = <ReportSheets webinarId={detail.id} title={detail.title} r={r} />;

  if (layout === "side") {
    return (
      <>
        <Panes three className="rm-split rm-ses">
          <Pane kind="list" keep="session-list" label="Past sessions">
            <Page layout="side" className="rm">
              {nav}
              <PastSessions detail={detail} current={live.sessionId} />
            </Page>
          </Pane>
          <Pane kind="main" showing={live.sessionId} label="The session">
            <Page layout="side" className="rm">
              {title}
              <div className="rm-body">
                <ReportMain live={live} r={r} layout={layout} webinarId={detail.id} />
              </div>
            </Page>
          </Pane>
          <Pane kind="detail" showing={live.sessionId} label="The replay and the room's reactions">
            <Page layout="side" className="rm">
              <ReportRest live={live} r={r} layout={layout} />
            </Page>
          </Pane>
        </Panes>
        {sheets}
      </>
    );
  }
  return (
    <Page layout={layout} narrow className="rm">
      {nav}
      {title}
      <div className="rm-body">
        <ReportMain live={live} r={r} layout={layout} webinarId={detail.id} />
        <ReportRest live={live} r={r} layout={layout} />
      </div>
      {layout === "phone" && r.waiting.length ? (
        <DockedBar>
          <Button variant="p" full icon="sparkle" onClick={() => r.setReplying(true)}>
            Send replies
          </Button>
        </DockedBar>
      ) : null}
      {sheets}
    </Page>
  );
}
